import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, X, PackageCheck, Receipt, FileDown, ShoppingCart } from "lucide-react";

const PO_ST = {
  ordenada: { label: "Ordenada", color: "#38bdf8" },
  parcialmente_recibida: { label: "Parcialmente recibida", color: "#fbbf24" },
  recibida: { label: "Recibida", color: "#4ade80" },
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
    <div className="panel w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <h3 className="font-bold text-slate-100 text-sm uppercase tracking-widest">{title}</h3>
        <button onClick={onClose} className="btn-ghost p-1" data-testid="po-modal-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const NewPoModal = ({ req, onClose, onSaved }) => {
  const [vendors, setVendors] = useState([]);
  const [existing, setExisting] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/vendors", { params: { active: true } }).then(({ data }) => setVendors(data));
    api.get("/purchase-orders", { params: { requisition_id: req.id } }).then(({ data }) => {
      setExisting(data);
      const ordered = {};
      data.filter((p) => p.status !== "cancelada").forEach((p) => p.items.forEach((i) => { ordered[i.req_item_id] = (ordered[i.req_item_id] || 0) + i.quantity; }));
      setRows(req.items.map((it) => {
        const auth = it.qty_authorized == null ? it.quantity : it.qty_authorized;
        const pending = Math.max(0, auth - (ordered[it.id] || 0));
        return { req_item_id: it.id, name: it.product_name, unit: it.unit, pending, quantity: pending, unit_price: "" };
      }));
    });
  }, [req]);
  const total = rows.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0), 0);
  const save = async () => {
    if (!vendorId) return toast.error("Selecciona el proveedor");
    const items = rows.filter((r) => Number(r.quantity) > 0).map((r) => ({ req_item_id: r.req_item_id, quantity: Number(r.quantity), unit_price: Number(r.unit_price) || 0 }));
    if (!items.length) return toast.error("Indica cantidades a ordenar");
    setBusy(true);
    try {
      await api.post("/purchase-orders", { requisition_id: req.id, vendor_id: vendorId, items, notes: notes || null });
      toast.success("Orden de compra generada");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Nueva OC desde ${req.folio}`} onClose={onClose}>
      <div className="space-y-3 text-xs">
        {existing.length > 0 && <div className="text-[10px] text-slate-500">Esta requisición ya tiene {existing.length} OC(s); solo se muestran cantidades pendientes.</div>}
        <select data-testid="po-vendor" className="input-cyber px-3 py-2 text-sm w-full" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
          <option value="">Proveedor…</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        {rows.map((r, i) => (
          <div key={r.req_item_id} className="flex items-center gap-2">
            <span className="flex-1 text-slate-200 truncate">{r.name} <span className="text-slate-500">(pend. {r.pending} {r.unit})</span></span>
            <input type="number" min="0" max={r.pending} step="any" className="input-cyber px-2 py-1 w-20 text-right" data-testid={`po-qty-${i}`}
              value={r.quantity} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} />
            <input type="number" min="0" step="any" placeholder="P.U. $" className="input-cyber px-2 py-1 w-24 text-right" data-testid={`po-price-${i}`}
              value={r.unit_price} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)))} />
          </div>
        ))}
        <input className="input-cyber px-2 py-1.5 w-full" placeholder="Notas / condiciones (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <div className="flex justify-between items-center">
          <span className="font-mono text-sm font-bold" style={{ color: "#86efac" }}>Total {fmtMXN(total)}</span>
          <button data-testid="po-create" disabled={busy} onClick={save} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <ShoppingCart className="w-3.5 h-3.5 inline mr-1" />} Generar OC
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ReceiveModal = ({ po, onClose, onDone }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [dest, setDest] = useState("obra");
  const [whId, setWhId] = useState("");
  const [inv, setInv] = useState("");
  const [rows, setRows] = useState(po.items.map((it) => ({ req_item_id: it.req_item_id, name: it.product_name, unit: it.unit, pending: Math.max(0, it.quantity - (it.qty_received || 0)), qty: "" })));
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/warehouses").then(({ data }) => setWarehouses(data.filter((w) => w.active !== false))); }, []);
  const save = async () => {
    const items = rows.filter((r) => Number(r.qty) > 0).map((r) => ({ req_item_id: r.req_item_id, qty: Number(r.qty) }));
    if (!items.length) return toast.error("Indica cantidades recibidas");
    if (dest === "almacen" && !whId) return toast.error("Selecciona el almacén");
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/receive`, { items, destination: dest, warehouse_id: dest === "almacen" ? whId : null, invoice_number: inv || null });
      toast.success(dest === "almacen" ? `Recibido · ${data.stock_entries} entrada(s) al almacén` : "Recibido directo a obra");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Recibir material · ${po.folio}`} onClose={onClose}>
      <div className="space-y-3 text-xs">
        <div className="flex gap-1">
          {[["obra", "Directo a obra"], ["almacen", "Entrada a almacén"]].map(([v, l]) => (
            <button key={v} data-testid={`receive-dest-${v}`} onClick={() => setDest(v)}
              className="px-3 py-1.5 text-[10px] uppercase tracking-widest rounded-sm border"
              style={{ borderColor: dest === v ? "var(--cyan)" : "var(--border)", color: dest === v ? "var(--cyan)" : "var(--text-dim)" }}>{l}</button>
          ))}
        </div>
        {dest === "almacen" && (
          <select data-testid="receive-warehouse" className="input-cyber px-2 py-1.5 w-full" value={whId} onChange={(e) => setWhId(e.target.value)}>
            <option value="">Almacén destino…</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
        {rows.map((r, i) => (
          <div key={r.req_item_id} className="flex items-center gap-2">
            <span className="flex-1 text-slate-200 truncate">{r.name} <span className="text-slate-500">(pend. {r.pending} {r.unit})</span></span>
            <input type="number" min="0" max={r.pending} step="any" className="input-cyber px-2 py-1 w-24 text-right" data-testid={`receive-qty-${i}`}
              value={r.qty} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
          </div>
        ))}
        <input className="input-cyber px-2 py-1.5 w-full" placeholder="No. de factura / remisión (opcional)" value={inv} onChange={(e) => setInv(e.target.value)} data-testid="receive-invoice" />
        <div className="flex justify-end">
          <button data-testid="receive-confirm" disabled={busy} onClick={save} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <PackageCheck className="w-3.5 h-3.5 inline mr-1" />} Registrar recepción
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default function PurchaseOrdersTab({ user, fromReq, clearFromReq }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFrom, setNewFrom] = useState(null);
  const [receiveFor, setReceiveFor] = useState(null);
  const load = () => {
    api.get("/purchase-orders").then(({ data }) => setRows(data))
      .catch(() => toast.error("No se pudieron cargar las órdenes"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { if (fromReq) { setNewFrom(fromReq); clearFromReq(); } }, [fromReq, clearFromReq]);

  const genExpense = async (po) => {
    if (!window.confirm(`Generar el gasto/CxP de ${po.folio} por ${fmtMXN(po.total)} (quedará en compromisos por pagar)?`)) return;
    try {
      await api.post(`/purchase-orders/${po.id}/generate-expense`, {});
      toast.success("Gasto generado — aparece en Compromisos por pagar");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  return (
    <div className="space-y-2" data-testid="po-tab">
      {rows.length === 0 && (
        <div className="panel p-8 text-center text-slate-400 text-sm">
          <Receipt className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          Sin órdenes de compra. Genera una desde una requisición autorizada (botón "→ OC").
        </div>
      )}
      {rows.map((po) => {
        const st = PO_ST[po.status] || PO_ST.ordenada;
        const received = po.items.reduce((s, i) => s + (i.qty_received || 0), 0);
        const totalQty = po.items.reduce((s, i) => s + i.quantity, 0);
        return (
          <div key={po.id} className="panel px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`po-${po.id}`}>
            <span className="font-mono text-sm font-bold text-slate-100">{po.folio}</span>
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}66` }} data-testid={`po-status-${po.id}`}>{st.label}</span>
            <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
              {fmtDate(po.created_at)} · Req <span className="text-slate-300">{po.requisition_folio}</span> · {po.vendor_name} · {received}/{totalQty} recibido
            </div>
            <div className="font-mono text-sm font-bold text-slate-100">{fmtMXN(po.total)}</div>
            <div className="flex items-center gap-1 flex-wrap">
              {po.status !== "recibida" && (
                <button data-testid={`po-receive-${po.id}`} onClick={() => setReceiveFor(po)} className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">
                  <PackageCheck className="w-3 h-3 inline" /> Recibir
                </button>
              )}
              {!po.expense_id ? (
                <button data-testid={`po-expense-${po.id}`} onClick={() => genExpense(po)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#f59e0b", border: "1px solid #f59e0b" }}>
                  <FileDown className="w-3 h-3 inline" /> Generar gasto
                </button>
              ) : (
                <span className="text-[10px] font-mono" style={{ color: "#86efac" }}>✓ gasto generado</span>
              )}
            </div>
            {(po.receipts || []).length > 0 && (
              <div className="w-full text-[10px] font-mono text-slate-500">
                {po.receipts.map((r) => `📦 ${new Date(r.at).toLocaleDateString("es-MX")} ${r.destination === "almacen" ? `→ ${r.warehouse_name}` : "→ obra"} (${r.by_name})`).join(" · ")}
              </div>
            )}
          </div>
        );
      })}
      {newFrom && <NewPoModal req={newFrom} onClose={() => setNewFrom(null)} onSaved={() => { setNewFrom(null); load(); }} />}
      {receiveFor && <ReceiveModal po={receiveFor} onClose={() => setReceiveFor(null)} onDone={() => { setReceiveFor(null); load(); }} />}
    </div>
  );
}
